import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { AGENT_ICON_SRC, agentColors, glassPanelStyle } from './agentChatLayout'
import AgentStatusStrip from './AgentStatusStrip'
import { buildCitationRegistry, formatReferenceLine } from './agentCitationRender'

const HIGHLIGHT_MS = 2000

const refButtonStyle = {
  display: 'inline',
  margin: 0,
  padding: '0 2px',
  border: 'none',
  background: 'transparent',
  color: agentColors.accent,
  fontSize: '0.75em',
  fontWeight: 600,
  verticalAlign: 'super',
  lineHeight: 0,
  cursor: 'pointer',
  textDecoration: 'none',
}

const refRowBase = {
  fontSize: 12,
  lineHeight: 1.45,
  padding: '6px 8px',
  borderRadius: 6,
  border: '1px solid transparent',
  transition: 'background 0.2s ease, border-color 0.2s ease',
}

const refRowHighlight = {
  background: 'rgba(193, 127, 89, 0.18)',
  borderColor: agentColors.accent,
}

function AssistantMessageBody({ messageId, content, citations, canOpenWiki, streaming }) {
  const [highlightedNum, setHighlightedNum] = useState(null)
  const refRowsRef = useRef({})
  const highlightTimerRef = useRef(null)

  const parsed = useMemo(() => {
    if (streaming || !content) return { registry: [], segments: [{ type: 'text', value: content || '' }] }
    return buildCitationRegistry(content, citations)
  }, [content, citations, streaming])

  const focusRef = useCallback((num) => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    setHighlightedNum(num)
    refRowsRef.current[num]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    highlightTimerRef.current = setTimeout(() => {
      setHighlightedNum(null)
      highlightTimerRef.current = null
    }, HIGHLIGHT_MS)
  }, [])

  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current)
    }
  }, [])

  if (streaming && !content?.trim()) {
    return null
  }

  if (streaming) {
    return (
      <>
        {content}
        <span style={{ display: 'inline-block', marginLeft: 1, color: agentColors.accent, opacity: 0.85 }}>
          ▍
        </span>
      </>
    )
  }

  const { registry, segments } = parsed

  return (
    <>
      <div style={{ whiteSpace: 'pre-wrap' }}>
        {segments.map((seg, i) => {
          if (seg.type === 'ref' && seg.num != null) {
            return (
              <button
                key={`ref-${i}-${seg.num}`}
                type="button"
                style={refButtonStyle}
                title={`Jump to reference [${seg.num}]`}
                onClick={() => focusRef(seg.num)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = 'underline'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = 'none'
                }}
              >
                [{seg.num}]
              </button>
            )
          }
          return <span key={`text-${i}`}>{seg.value}</span>
        })}
      </div>

      {registry.length > 0 ? (
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: `1px solid ${agentColors.borderSoft}`,
          }}
        >
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: agentColors.textMuted,
              marginBottom: 6,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            References
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {registry.map((entry) => {
              const { num, citation } = entry
              const isWiki = citation.type === 'wiki' && citation.slug
              const rowStyle = {
                ...refRowBase,
                ...(highlightedNum === num ? refRowHighlight : {}),
                cursor: 'pointer',
              }

              const label = (
                <>
                  <span style={{ fontWeight: 600, color: agentColors.accent }}>[{num}]</span>{' '}
                  {formatReferenceLine(citation)}
                </>
              )

              if (isWiki && canOpenWiki) {
                return (
                  <Link
                    key={`${messageId}-ref-${num}`}
                    id={`msg-${messageId}-ref-${num}`}
                    ref={(el) => {
                      refRowsRef.current[num] = el
                    }}
                    to={`/admin/knowledge/wiki?tab=wiki&slug=${encodeURIComponent(citation.slug)}`}
                    style={{ ...rowStyle, textDecoration: 'none', color: agentColors.textPrimary }}
                    onClick={() => focusRef(num)}
                  >
                    {label}
                  </Link>
                )
              }

              return (
                <div
                  key={`${messageId}-ref-${num}`}
                  id={`msg-${messageId}-ref-${num}`}
                  ref={(el) => {
                    refRowsRef.current[num] = el
                  }}
                  role="button"
                  tabIndex={0}
                  style={rowStyle}
                  onClick={() => focusRef(num)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      focusRef(num)
                    }
                  }}
                >
                  {label}
                </div>
              )
            })}
          </div>
        </div>
      ) : null}
    </>
  )
}

function MessageBubble({ isUser, messageId, content, citations, canOpenWiki, streaming }) {
  return (
    <div
      style={{
        maxWidth: '100%',
        padding: '12px 16px',
        borderRadius: isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
        background: isUser ? agentColors.userBubble : 'rgba(255,255,255,0.72)',
        color: isUser ? '#fafaf9' : agentColors.textPrimary,
        fontSize: 14,
        lineHeight: 1.6,
        border: isUser ? 'none' : `1px solid ${agentColors.assistantBorder}`,
        boxShadow: isUser ? 'none' : '0 1px 2px rgba(28, 25, 23, 0.04)',
        ...(streaming && !isUser ? { minHeight: 48 } : {}),
      }}
    >
      {isUser ? (
        <div style={{ whiteSpace: 'pre-wrap' }}>{content}</div>
      ) : (
        <AssistantMessageBody
          messageId={messageId}
          content={content}
          citations={citations}
          canOpenWiki={canOpenWiki}
          streaming={streaming}
        />
      )}
    </div>
  )
}

function ThreadSkeleton() {
  return (
    <div style={{ marginLeft: 42, marginBottom: 16 }}>
      {[1, 2].map((i) => (
        <div
          key={i}
          style={{
            ...glassPanelStyle,
            height: 56,
            marginBottom: 8,
            opacity: 0.55,
          }}
        />
      ))}
    </div>
  )
}

function messageKey(m) {
  return m.id || m.client_id || `${m.role}-${m.created_at}`
}

function AssistantTurnRow({ children }) {
  return (
    <div
      style={{ marginBottom: 16, display: 'flex', gap: 10, alignItems: 'flex-start' }}
    >
      <img
        src={AGENT_ICON_SRC}
        alt=""
        width={32}
        height={32}
        style={{
          flexShrink: 0,
          borderRadius: '50%',
          border: `2px solid ${agentColors.borderSoft}`,
          background: agentColors.bgWarm,
        }}
      />
      <div style={{ flex: 1, minWidth: 0, maxWidth: '88%' }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: agentColors.textSecondary,
            marginBottom: 4,
          }}
        >
          Knowledge Assistant
        </div>
        {children}
      </div>
    </div>
  )
}

export default function AgentChatThread({
  messages,
  agentActivity,
  canOpenWiki,
  sending,
  messagesLoading = false,
}) {
  const bottomRef = useRef(null)
  const containerRef = useRef(null)
  const scrollTickRef = useRef(0)
  const lastMsg = messages[messages.length - 1]
  const isStreaming = lastMsg?.role === 'assistant' && lastMsg?.streaming
  const streamingAssistant = useMemo(
    () => [...messages].reverse().find((m) => m.role === 'assistant' && m.streaming),
    [messages]
  )
  const showStatusTurnSlot = sending && agentActivity

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120
    if (isStreaming && !nearBottom) return

    scrollTickRef.current += 1
    if (isStreaming && scrollTickRef.current % 2 !== 0) return

    bottomRef.current?.scrollIntoView({ behavior: isStreaming ? 'auto' : 'smooth' })
  }, [messages, agentActivity, sending, messagesLoading, isStreaming])

  return (
    <div
      ref={containerRef}
      style={{
        flex: 1,
        overflowY: 'auto',
        padding: '16px 20px',
        background: 'transparent',
        position: 'relative',
      }}
    >
      {messages.length === 0 && !sending && !messagesLoading ? (
        <div
          style={{
            fontSize: 14,
            color: agentColors.textSecondary,
            lineHeight: 1.6,
            maxWidth: 400,
            marginTop: 24,
          }}
        >
          Ask about SOPs, contracts, wiki pages, or client docs. I&apos;ll search within your
          permissions and cite sources.
        </div>
      ) : null}

      {messages.map((m) => {
        const isUser = m.role === 'user'
        const key = messageKey(m)
        const bubbleId = m.id || m.client_id || key
        if (isUser) {
          return (
            <div
              key={key}
              style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}
            >
              <div style={{ maxWidth: '88%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: agentColors.textSecondary,
                    marginBottom: 4,
                    paddingRight: 2,
                  }}
                >
                  Me
                </div>
                <MessageBubble isUser content={m.content} messageId={bubbleId} />
              </div>
            </div>
          )
        }

        const isStatusOnlyTurn =
          m.streaming &&
          showStatusTurnSlot &&
          streamingAssistant?.client_id === m.client_id

        if (isStatusOnlyTurn) {
          return (
            <AssistantTurnRow key={key}>
              <AgentStatusStrip activity={agentActivity} />
            </AssistantTurnRow>
          )
        }

        if (m.streaming && !m.content?.trim()) {
          return null
        }

        return (
          <AssistantTurnRow key={key}>
            <MessageBubble
              isUser={false}
              messageId={bubbleId}
              content={m.content}
              citations={m.metadata?.citations}
              canOpenWiki={canOpenWiki}
              streaming={m.streaming}
            />
          </AssistantTurnRow>
        )
      })}

      {showStatusTurnSlot && !streamingAssistant ? (
        <AssistantTurnRow>
          <AgentStatusStrip activity={agentActivity} />
        </AssistantTurnRow>
      ) : null}

      {messagesLoading ? <ThreadSkeleton /> : null}

      <div ref={bottomRef} />
    </div>
  )
}
